'use client';
import { createContext, useContext, useMemo, useState } from 'react';

/**
 * LightboxContext
 * Manages the open state and current image for the Lightbox component.
 * Provides functions to open, close, and update the current image in the lightbox.
 */
export const LightboxContext = createContext(null);
export const LightboxActionsContext = createContext(null);

export function LightboxProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const openLightbox = () => {
    setIsOpen(true);
  };

  const closeLightbox = () => {
    setIsOpen(false);
    setCurrentImage(null);
  };

  const updateCurrentImage = (image) => {
    setCurrentImage(image);
  };

  const stateValue = useMemo(() => ({ isOpen, currentImage }), [isOpen, currentImage]);

  const actionsValue = useMemo(
    () => ({ openLightbox, closeLightbox, updateCurrentImage }),
    [openLightbox, closeLightbox, updateCurrentImage]
  );

  return (
    <LightboxContext.Provider value={stateValue}>
      <LightboxActionsContext.Provider value={actionsValue}>{children}</LightboxActionsContext.Provider>
    </LightboxContext.Provider>
  );
}

export function useLightboxState() {
  const context = useContext(LightboxContext);
  if (!context) {
    throw new Error('useLightboxState must be used within LightboxProvider');
  }
  return context;
}

export function useLightboxActions() {
  const context = useContext(LightboxActionsContext);
  if (!context) {
    throw new Error('useLightboxActions must be used within LightboxProvider');
  }
  return context;
}
